import { normalizePhone } from "@elektr/shared";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState, type FormEvent } from "react";

import { Button } from "@/components/ui/button";
import { Dialog } from "@/components/ui/dialog";
import { Field, Input } from "@/components/ui/form";
import { useToast } from "@/components/ui/toast";
import { api, errorMessage, unwrap } from "@/lib/api";
import { useT } from "@/lib/i18n";

/** Uyni boshqa telefon raqamiga o'tkazish so'rovi (egasi yoki admin). Yangi egasi OTP bilan tasdiqlaydi. */
export function TransferDialog({
  propertyId,
  open,
  onOpenChange,
}: {
  propertyId: string;
  open: boolean;
  onOpenChange: (o: boolean) => void;
}) {
  const { t } = useT();
  const toast = useToast();
  const qc = useQueryClient();
  const [phone, setPhone] = useState("");
  const [error, setError] = useState<string | null>(null);

  const start = useMutation({
    mutationFn: async () => {
      const normalized = normalizePhone(phone);
      if (!normalized) throw new Error("phone");
      return unwrap(
        await api.POST("/api/properties/{id}/transfer/", {
          params: { path: { id: propertyId } },
          body: { to_phone: normalized },
        }),
      );
    },
    onSuccess: () => {
      toast.success(t("transfer.started"));
      setPhone("");
      setError(null);
      onOpenChange(false);
      void qc.invalidateQueries({ queryKey: ["transfers", propertyId] });
      void qc.invalidateQueries({ queryKey: ["my-properties"] });
    },
    onError: (e) =>
      setError(
        e instanceof Error && e.message === "phone"
          ? t("auth.phone_invalid")
          : errorMessage(e, t("common.error")),
      ),
  });

  return (
    <Dialog
      open={open}
      onOpenChange={onOpenChange}
      title={t("transfer.title")}
      description={t("transfer.description")}
    >
      <form
        className="flex flex-col gap-4"
        noValidate
        onSubmit={(e: FormEvent) => {
          e.preventDefault();
          setError(null);
          start.mutate();
        }}
      >
        <Field label={t("transfer.new_owner_phone")} hint={t("auth.phone_hint")}>
          {(id) => (
            <Input
              id={id}
              type="tel"
              inputMode="tel"
              placeholder="90 123 45 67"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
            />
          )}
        </Field>
        {error && (
          <p role="alert" className="text-sm text-danger">
            {error}
          </p>
        )}
        <div className="flex justify-end gap-2">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            {t("common.cancel")}
          </Button>
          <Button type="submit" disabled={start.isPending}>
            {t("transfer.send")}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}
